/*
  This file is used for global style overrides.
  Global overrides should be used to modify "base" components that are used by other components under the hood,
  such as the ButtonBase (used by Buttons) and Backdrop (used by Modals + Dialogs).

  See https://mui.com/customization/theme-components/#global-style-overrides for more information.

  For components that are used by consumers directly, such as the Avatar or Label, 
  the styles for that component belong in a `componentName.styled.ts` file within that components directory, ie Avatar.styled.ts.
*/
import { typography } from './typography';

const MuiCssBaseline = {
  // this creates a global `sr-only` CSS class name that can be used in any component to visually hide the element while preserving accessibility
  styleOverrides: {
    '.sr-only': {
      position: 'absolute !important',
      overflow: 'hidden !important',
      clip: 'rect(0 0 0 0) !important',
      height: '1px !important',
      width: '1px !important',
      margin: '-1px !important',
      padding: '0 !important',
      border: '0 !important',
    },
  },
};

const MuiButton = {
  styleOverrides: {
    root: `
      text-transform: none;
    `,
  },
};

const MuiButtonBase = {
  defaultProps: {
    disableRipple: true,
  },
};

const MuiOutlinedInput = {
  defaultProps: {
    // In order to use { notched: true, shrink: true } on the <TextField>, those props need to be set at the theme level due to some inherent TextField behavior
    // https://github.com/mui-org/material-ui/issues/20000#issuecomment-595178971
    notched: false,
  },
};

const MuiInputLabel = {
  defaultProps: {
    // In order to use { notched: true, shrink: true } on the <TextField>, those props need to be set at the theme level due to some inherent TextField behavior
    // https://github.com/mui-org/material-ui/issues/20000#issuecomment-595178971
    shrink: true,
  },
};

const MuiInputBase = {
  styleOverrides: {
    input: {
      // targets Chrome/Edge
      '&:-webkit-autofill': {
        // overrides the blue background color when Chrome is autofilling an input. This comes from a discussion with Jo Wang, the designer for the TextField component
        WebkitBoxShadow: '0 0 0 100px #fff inset',
        fontFamily: typography.fontFamily,
        /*
          NOTE: It is not currently feasible to override the font-size of the input field when autofill is active without creating a security vulnerability -- 
          this is why Chrome overwrites the font-size via the User Agent Stylesheet .
          Historical context is available here https://stackoverflow.com/a/57147062 and here https://bugs.chromium.org/p/chromium/issues/detail?id=916838#c16
          
          There is a commit in Chromium that may enable the ability to override the font-size in the future https://chromium.googlesource.com/chromium/src.git/+/39f06061af8da287363cba093071ec348ef642c2,
          but currently we just have to accept that the font-size during autofill will be incorrect.
        */
      },
      // targets non-Chrome/Edge
      '&:autofill': {
        WebkitBoxShadow: '0 0 0 100px #fff inset',
        fontFamily: typography.fontFamily,
      },
    },
  },
};

export const components = {
  MuiButton,
  MuiButtonBase,
  MuiCssBaseline,
  MuiInputBase,
  MuiInputLabel,
  MuiOutlinedInput,
};
